
import React from "react";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/context/LanguageContext";
import { HelpCircle } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

export type SortOption = 'rating-high' | 'rating-low' | 'rent-high' | 'rent-low' | 'reviews-high' | 'reviews-low';

interface SortOptionsProps {
  sortOption: SortOption;
  onSortChange: (option: SortOption) => void;
  isOpen?: boolean;
  onClose?: () => void;
  displayAsButtons?: boolean;
}

export const SortOptions = ({ 
  sortOption, 
  onSortChange,
  isOpen,
  onClose,
  displayAsButtons = false
}: SortOptionsProps) => {
  const { t, language } = useLanguage();
  
  const renderSortHint = () => {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <HelpCircle className="h-4 w-4 ml-1 text-muted-foreground" />
          </TooltipTrigger>
          <TooltipContent>
            <p className="text-xs max-w-52">
              {language === 'en' 
                ? "Sort options determine the order of results: highest rating first, lowest rating first, etc." 
                : "排序選項決定結果的順序：最高評分優先、最低評分優先等。"}
            </p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  };

  return (
    <div className="flex flex-col gap-2 w-full">
      <div className="flex items-center mb-2">
        <h3 className="text-sm font-medium">{t('sortBy')}</h3>
        {renderSortHint()}
      </div>
      <Button
        variant={sortOption === "rating-high" ? "default" : "outline"}
        className="w-full justify-start py-6"
        onClick={() => onSortChange("rating-high")}
      >
        {t('sortRatingHighest')}
      </Button>
      <Button
        variant={sortOption === "rating-low" ? "default" : "outline"}
        className="w-full justify-start py-6"
        onClick={() => onSortChange("rating-low")}
      >
        {t('sortRatingLowest')}
      </Button>
      <Button
        variant={sortOption === "rent-high" ? "default" : "outline"}
        className="w-full justify-start py-6"
        onClick={() => onSortChange("rent-high")}
      >
        {t('sortRentHighest')}
      </Button>
      <Button
        variant={sortOption === "rent-low" ? "default" : "outline"}
        className="w-full justify-start py-6"
        onClick={() => onSortChange("rent-low")}
      >
        {t('sortRentLowest')}
      </Button>
      <Button
        variant={sortOption === "reviews-high" ? "default" : "outline"}
        className="w-full justify-start py-6"
        onClick={() => onSortChange("reviews-high")}
      >
        {t('sortReviewsHighest')}
      </Button>
      <Button
        variant={sortOption === "reviews-low" ? "default" : "outline"}
        className="w-full justify-start py-6"
        onClick={() => onSortChange("reviews-low")}
      >
        {t('sortReviewsLowest')}
      </Button>
    </div>
  );
};

export default SortOptions;
